import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MarkdownPage from '../components/MarkdownPage';
import { appUrl } from '../properties/properties';

const ApiDocFallback = () => {
  const defaultInfo = {
    method: "GET",
    auth: "No authentication required",
    headers: "No custom headers",
    requestBody: "Not applicable"
  };

  const apiGroups = [
    {
      title: "Frontpage",
      endpoints: [
        {
          name: "Show latest vulnerabilities",
          url: "/api/lastvulnerabilities",
          example: "/api/lastvulnerabilities",
          responseLimit: "Maximum 8 records"
        },
        {
          name: "Show latest exploited vulnerabilities",
          url: "/api/exploitedvulnerabilities",
          example: "/api/exploitedvulnerabilities",
          responseLimit: "Maximum 8 records"
        },
        {
          name: "Show latest critical vulnerabilities",
          url: "/api/criticalvulnerabilities",
          example: "/api/criticalvulnerabilities",
          responseLimit: "Maximum 8 records"
        }
      ]
    },
    {
      title: "Scores & Filters",
      url: "/api/search",
      example: "/api/search?fromScore=0&toScore=10",
      commonParams: [
        "fromScore (0-10, e.g., fromScore=7.5)",
        "toScore (0-10, e.g., toScore=10)",
        "fromEpss (0-100, e.g., fromEpss=20)",
        "toEpss (0-100, e.g., toEpss=90)",
        "fromDate (YYYY-MM-DD, e.g., fromDate=2023-01-14)",
        "toDate (YYYY-MM-DD, e.g., toDate=2025-01-14)",
        "fromUpdatedDate (YYYY-MM-DD, e.g., fromUpdatedDate=2023-01-14)",
        "toUpdatedDate (YYYY-MM-DD, e.g., toUpdatedDate=2025-01-14)",
        "product (string, one product name, e.g., product=Windows). Every word must appear in the name, and the last word also matches longer words (product=Win finds Windows).",
        "vendor (string, one vendor name, e.g., vendor=Microsoft). Matched like product. Not a list: vendor=Siemens,Schneider looks for a single vendor whose name contains both words.",
        "advisory (string, an advisory ID, e.g., advisory=cisco-sa-ata19x-multi-RDTEqRsy). Matched like product.",
        "assigner (comma-separated list, e.g., assigner=mitre or assigner=mitre,redhat)",
        "exploited (true/false, e.g., exploited=true)",
        "fromKEVDate (YYYY-MM-DD, e.g., fromKEVDate=2024-01-01). Date added to a Known Exploited Vulnerabilities catalogue.",
        "toKEVDate (YYYY-MM-DD, e.g., toKEVDate=2024-12-31)",
        "text (keywords, e.g., text=vulnerability). Searches across description, EUVD ID, aliases, product name, and vendor name.",
        "page (integer, starts at 0, e.g., page=2)",
        "size (integer, default 10, e.g., size=100 (max))"
      ],
      endpoints: [
        { name: "Query Vulnerabilities with Flexible Filters",
         responseLimit: "Maximum 100 records per request",
         notes: "An invalid parameter returns 400. Errors are JSON: {\"error\": \"...\"}." }
      ]
    },
    {
      title: "Specific Resources",
      endpoints: [
        {
          name: "Show EUVD by ID",
          url: "/api/enisaid",
          example: "/api/enisaid?id=CVE-2024-3400",
          params: ["id (string, an EUVD ID or a vulnerability ID such as a CVE, e.g., id=EUVD-2025-4893 or id=CVE-2024-3400)"],
          notes: "Returns 404 with {\"error\": \"EUVD not found\"} if no EUVD matches the ID."
        },
        {
          name: "Show advisory by ID",
          url: "/api/advisory",
          example: "/api/advisory?id=cisco-sa-ata19x-multi-RDTEqRsy",
          params: ["id (string, e.g., id=oxas-adv-2024-0002)"],
          notes: "Returns 404 with {\"error\": \"Advisory not found\"} if no advisory matches the ID."
        }

      ]
    },
    {
      title: "Data Downloads",
      endpoints: [
        {
          name: "Download CVE-to-EUVD ID Mapping (CSV)",
          url: "/api/dump/cve-euvd-mapping",
          example: "/api/dump/cve-euvd-mapping",
          responseLimit: "Full dataset (CSV with columns: euvd_id, cve_id). Contains only records linked to published CVEs. Updated daily at 07:00 UTC.",
          isDownload: true,
          downloadUrl: "/dump/cve-euvd-mapping",
          downloadLabel: "Download Latest CSV"
        },
        {
          name: "Download KEV Dump (JSON)",
          url: "/api/kev/dump",
          example: "/api/kev/dump",
          responseLimit: "Full dataset (JSON). Consolidated Known Exploited Vulnerabilities from CISA KEV and ENISA EU KEV sources. Updated daily at 07:00 UTC.",
          isDownload: true,
          downloadUrl: "/kev/dump",
          downloadLabel: "Download Latest JSON",
          responseFields: [
            "cveId — CVE identifier (e.g., CVE-2021-22555)",
            "euvdId — EUVD identifier (e.g., EUVD-2021-9696)",
            "dateAdded — Earliest date the CVE was added across all KEV sources (format: YYYY-MM-DD)",
            "sources — List of KEV sources that include this CVE (e.g., cisa_kev, eukev_kev)"
          ]
        }
      ]
    }
  ];

  return (
    <div className=" container-1200 container-up  container-hd p-8 space-y-12 bg-gradient-to-b from-white to-gray-50 min-h-screen">
      <h1 className="text-4xl font-extrabold text-center text-blue-900 mb-10 underline decoration-blue-400">EUVD API Documentation</h1>
      {apiGroups.map((group, i) => (
        <div key={i} className="border-l-4 border-blue-400 bg-white p-6 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold text-blue-800 mb-6">{group.title}</h2>
          {group.endpoints.map((endpoint, j) => (
            <div key={j} className="mb-10 p-4 border rounded-xl bg-gray-50 hover:bg-white transition">
              <h3 className="text-xl font-bold text-blue-700 mb-2">{endpoint.name}</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Endpoint:</strong> <code className="text-blue-600">{endpoint.url || group.url}</code></p>
                <p><strong>Method:</strong> {defaultInfo.method}</p>
                <p><strong>Authentication:</strong> {defaultInfo.auth}</p>
                <p><strong>Request Headers:</strong> {defaultInfo.headers}</p>
                <p><strong>Request Body:</strong> {defaultInfo.requestBody}</p>
                { (endpoint.responseLimit || defaultInfo.responseLimit) && (
                <p><strong>Response Size Limit:</strong> {endpoint.responseLimit || defaultInfo.responseLimit}</p>
                )}
                {endpoint.notes && (
                <p><strong>Notes:</strong> {endpoint.notes}</p>
                )}
              </div>
              {(group.commonParams || endpoint.params) && (
                <div className="mt-3">
                  <p className="font-medium text-gray-800">Parameters:</p>
                  <ul className="list-disc ml-6 text-sm text-gray-700">
                    {(endpoint.params || group.commonParams).map((param, idx) => (
                      <li key={idx}>{param}</li>
                    ))}
                  </ul>
                </div>
              )}
              {endpoint.responseFields && (
                <div className="mt-3">
                  <p className="font-medium text-gray-800">Response Fields:</p>
                  <ul className="list-disc ml-6 text-sm text-gray-700">
                    {endpoint.responseFields.map((field, idx) => (
                      <li key={idx}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-4 text-sm">
                <p className="font-semibold text-gray-800">Curl Example:</p>
                <pre className="bg-gray-100 p-3 rounded-md text-gray-800 overflow-auto">
                  curl -X {defaultInfo.method} https://euvdservices.enisa.europa.eu{endpoint.example || group.example}
                </pre>
                <p className="font-semibold text-gray-800 mt-2">HTTP Request:</p>
                <pre className="bg-gray-100 p-3 rounded-md text-gray-800 overflow-auto">
                  {defaultInfo.method} https://euvdservices.enisa.europa.eu{endpoint.example || group.example} HTTP/1.1
                </pre>
              </div>
              {endpoint.isDownload && (
                <div className="mt-4">
                  <a
                    href={`${appUrl}${endpoint.downloadUrl || '/dump/cve-euvd-mapping'}`}
                    className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    download
                  >
                    {endpoint.downloadLabel || 'Download Latest CSV'}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const ApiDocumentation = () => {
  useEffect(() => {
    document.title = "API | EUVD";
  }, []);

  return (
    <>
      <Header />
      <div className='banner'>
        <nav className="breadcrumbs container-1200">
          <ul>
            <li><a href="/homepage">Home</a></li>
            <li><a href="">API documentation</a></li>
          </ul>
        </nav>
      </div>
      <MarkdownPage slug="apidoc" fallback={<ApiDocFallback />} />
      <Footer />
    </>
  );
};

export default ApiDocumentation;
